import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'

import {
  ALL_REDIRECTS_QUERY,
  SINGLE_REDIRECT_QUERY,
  REDIRECTS_DEPENDENTS_QUERY,
  CREATE_REDIRECT_MUTATION,
  UPDATE_REDIRECT_MUTATION,
  DELETE_REDIRECT_MUTATION,
  FORCE_DELETE_REDIRECT_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const redirectRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_REDIRECT_QUERY, variables).then((data: any) => {
      return hydrateObject(data.redirect)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_REDIRECTS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.redirects)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_REDIRECT_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.redirect),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_REDIRECTS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.redirects),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(REDIRECTS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([REDIRECTS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_REDIRECT_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.redirect)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_REDIRECT_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.redirect)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_REDIRECT_MUTATION, variables).then((data: any) => {
      return data.deleteRedirect
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_REDIRECT_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRedirect
    })
  },
}

export default redirectRepository
